import { SolutionScenario, SolutionCategory } from '@/types/scenarios/solution';
import { solutionCategories } from './categories';

// Import scenario files
import { automotive } from './scenarios/automotive';
import { marine } from './scenarios/marine';
import { appliances } from './scenarios/appliances';
import { officeEquipment } from './scenarios/office-equipment';
import { equipment } from './scenarios/equipment';
import { homeBuilders } from './scenarios/home-builders';
import { travelHospitality } from './scenarios/travel-hospitality';
import { manufacturing } from './scenarios/manufacturing';
import { industrial } from './scenarios/industrial';
import { mining } from './scenarios/mining';
import { trades } from './scenarios/trades';
import { realEstate } from './scenarios/real-estate';
import { events } from './scenarios/events';
import { clubsAssociations } from './scenarios/clubs-associations';

// Central registry of all solution scenarios
const solutionScenarioRegistry = new Map<string, SolutionScenario>();

// Register all solution scenarios
// Order: equipment-heavy verticals clustered together (HG ICP), then services / sales-led
[
  trades,
  automotive,
  marine,
  equipment,
  appliances,
  officeEquipment,
  manufacturing,
  industrial,
  mining,
  homeBuilders,
  travelHospitality,
  realEstate,
  clubsAssociations,
  events,
].forEach(scenario => {
  solutionScenarioRegistry.set(scenario.meta.id, scenario);
});

// Utility functions for accessing solution scenarios
export const getSolutionScenarioById = (id: string): SolutionScenario | undefined => {
  return solutionScenarioRegistry.get(id);
};

export const getSolutionScenarioBySlug = (slug: string): SolutionScenario | undefined => {
  return Array.from(solutionScenarioRegistry.values()).find(s => s.meta.slug === slug);
};

export const getSolutionScenariosByMarket = (market: string): SolutionScenario[] => {
  return Array.from(solutionScenarioRegistry.values()).filter(s => s.meta.market === market);
};

export const getAllSolutionScenarios = (): SolutionScenario[] => {
  return Array.from(solutionScenarioRegistry.values());
};

export const getFeaturedSolutionScenarios = (): SolutionScenario[] => {
  return Array.from(solutionScenarioRegistry.values()).filter(s => s.meta.isFeatured);
};

export const getRelatedSolutionScenarios = (scenarioId: string, limit = 3): SolutionScenario[] => {
  const scenario = getSolutionScenarioById(scenarioId);
  if (!scenario) return [];

  const related: SolutionScenario[] = [];
  const allScenarios = getAllSolutionScenarios();

  // First, get scenarios from same market
  const sameMarketScenarios = getSolutionScenariosByMarket(scenario.meta.market)
    .filter(s => s.meta.id !== scenarioId);

  // Shuffle and add up to limit
  const shuffled = sameMarketScenarios.sort(() => Math.random() - 0.5);
  related.push(...shuffled.slice(0, limit));

  // If still need more, get featured scenarios from other markets
  if (related.length < limit) {
    const otherScenarios = allScenarios
      .filter(s =>
        s.meta.id !== scenarioId &&
        s.meta.market !== scenario.meta.market &&
        !related.some(r => r.meta.id === s.meta.id) &&
        s.meta.isFeatured
      )
      .sort(() => Math.random() - 0.5);

    related.push(...otherScenarios.slice(0, limit - related.length));
  }

  // If still need more, get any other scenarios
  if (related.length < limit) {
    const remainingScenarios = allScenarios
      .filter(s =>
        s.meta.id !== scenarioId &&
        !related.some(r => r.meta.id === s.meta.id)
      )
      .sort(() => Math.random() - 0.5);

    related.push(...remainingScenarios.slice(0, limit - related.length));
  }

  return related.slice(0, limit);
};

export const searchSolutionScenarios = (query: string): SolutionScenario[] => {
  const lowerQuery = query.toLowerCase();
  return Array.from(solutionScenarioRegistry.values()).filter(scenario => {
    const meta = scenario.meta;
    return (
      meta.name.toLowerCase().includes(lowerQuery) ||
      meta.tagline.toLowerCase().includes(lowerQuery) ||
      meta.description.toLowerCase().includes(lowerQuery) ||
      meta.market.toLowerCase().includes(lowerQuery) ||
      meta.specificNeeds.some(n => n.toLowerCase().includes(lowerQuery))
    );
  });
};

export const getSolutionCategories = (): SolutionCategory[] => {
  return solutionCategories;
};

export const getSolutionCategoryById = (id: string): SolutionCategory | undefined => {
  return solutionCategories.find(cat => cat.id === id);
};

export const getSolutionCategoryBySlug = (slug: string): SolutionCategory | undefined => {
  return solutionCategories.find(cat => cat.slug === slug);
};

// Export the registry for advanced use cases
export { solutionScenarioRegistry };
